import { h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import backgroundPlaceholderSvg from '../../../assets/background-placeholder.svg';
import { CmsImageData, Properties } from '../../../common';
import { useDimension } from '../../../common/hooks/useDimension';
import { WithStyles, withStyles } from '../../../style';
import { isNotPresent, isPresent } from '../../../utils';
import * as styles from './BackgroundImage.scss';

/** Image ratio
 * desktop 21:9, laptop 16:9, tablet+mobile 4:3 */
const getImageSrc = (clientType: string, src: CmsImageData): string => {
  if (isNotPresent(src)) {
    return '';
  }
  // set 4x3 as default img
  let url;
  switch (clientType) {
    case 'laptop':
      url = src.responsiveImageLinksData?.landscape_ratio16x9['768'];
      break;
    case 'desktop':
      url = src.responsiveImageLinksData?.landscape_ratio21x9['768'];
      break;
    default:
      url = src.responsiveImageLinksData?.landscape_ratio4x3['768'];
      break;
  }

  return url?.startsWith('http') ? url : `https:${url}`;
};

const usePrevious = (value) => {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};

export interface BackgroundImageProps extends Omit<Properties, 'src'> {
  src: CmsImageData;
}

type EffectiveProps = BackgroundImageProps & WithStyles;

const BackgroundImage = withStyles(styles)(({ src, tx }: EffectiveProps) => {
  const [loaded, setLoaded] = useState(false);
  const [previewLoaded, setPreviewLoaded] = useState(false);

  const [contentDimension, contentRef] = useDimension();
  const [imageUrl, setImageUrl] = useState('');
  const [previewImageUrl, setPreviewImageUrl] = useState('');

  const prevUrl = usePrevious(imageUrl);

  useEffect(() => {
    const url = getImageSrc(contentDimension.clientType, src);
    const previewUrl = `${url}&imwidth=40`;

    if (url && url !== prevUrl) {
      // set the pic loading status when the src or client device changed
      const image: HTMLImageElement = new Image();
      const previewImage: HTMLImageElement = new Image();

      image.onload = () => {
        setLoaded(true);
      };

      previewImage.onload = () => {
        image.src = url;
        setImageUrl(url);
        setPreviewLoaded(true);
      };

      previewImage.src = previewUrl;
      setPreviewImageUrl(previewUrl);
      setLoaded(false);
      setPreviewLoaded(false);
    }
  }, [src, contentDimension.clientType]);

  return (
    <div class={tx('absolute grow--pos fill--pos overflow-hidden')} ref={contentRef}>
      <div
        class={tx('placeholder', 'grow fill', { fadeOut: isPresent(src) })}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: backgroundPlaceholderSvg }}
      />
      <div
        class={tx('preview', { previewLoaded: isPresent(previewImageUrl) && !loaded }, 'absolute grow--pos fill--pos')}
        // inline background style to avoid the background image render before css get evaluated
        style={{
          backgroundImage: `url(${previewImageUrl})`,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        }}
      />
      <div
        class={tx(
          'image',
          { imageLoaded: loaded || !previewLoaded, imageLoading: !previewLoaded },
          'absolute grow--pos fill--pos'
        )}
        style={{ backgroundImage: `url(${imageUrl})` }}
      />
    </div>
  );
});

export default BackgroundImage;
